import React, { useMemo } from 'react';
import { ListColumnType, ListFilterFieldType, ListMoreMenuConfig } from 'types/ListTypes';
import { BulkActionsConfig } from 'types/BulkActionTypes';
import AbstractList from 'components/lists/AbstractList';
import { useSettings } from 'services/settings/SettingsContext';
import ProjectModal from '../../views/jobs/ProjectModal';
import ProjectForm from './ProjectForm';
import { useTranslation } from 'react-i18next';

// Enable the bulk actions panel for this list
const projectBulkActions: BulkActionsConfig = {
    objectKey: 'project',
    deleteOrDeactivate: 'delete',
    showExport: true,
};

// Configure the more menu of this list
const projectMoreMenu: ListMoreMenuConfig = {
    inactiveItemsToggle: {
        semantic: 'deleted',
        activeParams: { deleted: true },
        clearFilterNames: ['closed'],
    },
};

// Set the filter fields of this list
const projectFilterFields: ListFilterFieldType[] = [
    {
        type: 'search',
        name: 'search',
        label: 'forms.search_label',
        placeholder: 'forms.search_placeholder',
        queryParam: 'search',
        width: 1.5
    },
    {
        type: 'dropdown',
        name: 'workflow',
        label: 'lists.filters.type_label',
        queryParam: 'workflow',
        apiRequest: {
            endpoint: 'get_workflow_list',
            params: { connected_feature: 'project', is_active: true },
            object: 'workflow'
        },
        groupByField: 'division',
        width: 1.25
    },
    {
        type: 'dropdown',
        name: 'closed',
        label: 'lists.filters.status_label',
        queryParam: 'closed',
        backendField: 'id',
        optionField: 'id',
        translationPrefix: 'project.closed_filter.',
        hardcodedOptions: [
            { id: 'false' },
            { id: 'true' },
        ],
        width: 1.25
    },
    {
        type: 'daterange',
        name: 'start_date_range',
        label: 'contract.general.start_date_label',
        queryParam: 'start_date',
        width: 2
    },
    {
        type: 'dropdown',
        name: 'division',
        label: 'lists.filters.division_label',
        queryParam: 'division',
        requiredFeature: 'divisions',
        defaultValueFromContext: 'user_division',
        apiRequest: {
            endpoint: 'get_division_list',
            params: { is_active: true },
            object: 'division'
        },
        width: 1.5
    }
];

const ProjectList: React.FC = () => {
    const { environmentSettings } = useSettings();
    const { t } = useTranslation();

    // Specify the options of this list:
    const query = {
        endpoint: 'get_project_list',
        defaultParams: {
            'closed': false,
            'deleted': false
        },
        object: 'project'
    }
    const objectName = 'project.general.object_name'

    // Set isPaginated to true if this list may have 100.000+ items:
    const isPaginated = true

    // Specify form, detail modal or detailpage for this list
    const formModal = <ProjectForm />;
    const modalSize = 'add-form';
    const detailModal = <ProjectModal />;
    const onRowClick = 'detailModal';

    // Render project title
    const renderProjectTitle = (title: string, client_name: string) => {
        return (
            <div>
                <span>{title}</span>
                <span> ({client_name})</span>
            </div>
        );
    }

    // Render the derived project status as a badge
    const renderProjectStatus = (status: string | null) => {
        if (!status) return <div />;
        return (
            <div className='badge-container'>
                <div className={`status-badge project-${status}`}>
                    {t(`project.status.${status}`)}
                </div>
            </div>
        );
    }

    // Specify the columns of this list
    const columns: ListColumnType[] = useMemo(() => [
        { 
            width: '1.5fr', 
            field: 'number', 
            label: 'project.general.project_number_label' 
        },
        { 
            width: '6fr', 
            field: 'name',
            orderField: 'name',
            label: 'project.general.name_label',
            render: (_, row) => renderProjectTitle(row.name, row.client_name) 
        },
        { 
            width: '2fr', 
            field: 'type_name', 
            orderField: 'workflow__name', 
            label: 'project.general.type_short_label' 
        },
        { 
            width: '144px', 
            field: 'status', 
            label: 'project.general.status_label',
            disableOrdering: true,
            render: (_, row) => renderProjectStatus(row.status) 
        },
    ], []);

    // Get the create jobs setting, to either hide or show the add button
    const { job_create_jobs } = environmentSettings;

    return (
        <AbstractList
            query={query}
            objectName={objectName}
            columns={columns}
            filterFields={projectFilterFields}
            formModal={formModal}
            modalSize={modalSize}
            detailModal={detailModal}
            isPaginated={isPaginated}
            onRowClick={onRowClick}
            showAddButton={job_create_jobs}
            detailModalSize='large-without-sidetab'
            bulkActions={projectBulkActions}
            moreMenu={projectMoreMenu}
        />
    );
};

export default ProjectList;